import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Bell,
  Plus,
  Check,
  X,
  Clock,
  Calendar,
  ArrowLeft,
  Loader2,
  AlertCircle,
  Trash2,
  RotateCcw,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to format relative time
const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date - now;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMs < 0) {
    // Overdue
    const absMins = Math.abs(diffMins);
    const absHours = Math.abs(diffHours);
    const absDays = Math.abs(diffDays);
    
    if (absDays > 0) return `${absDays} day${absDays > 1 ? 's' : ''} overdue`;
    if (absHours > 0) return `${absHours} hour${absHours > 1 ? 's' : ''} overdue`;
    return `${absMins} min${absMins !== 1 ? 's' : ''} overdue`;
  }

  if (diffDays > 0) return `in ${diffDays} day${diffDays > 1 ? 's' : ''}`;
  if (diffHours > 0) return `in ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
  if (diffMins > 0) return `in ${diffMins} min${diffMins !== 1 ? 's' : ''}`;
  return 'now';
};

// Helper to format date for display
const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Helper to format date for input
const formatDateForInput = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${mins}`;
};

export default function RemindersPage() {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('scheduled'); // scheduled, done, dismissed, all
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [newDueAt, setNewDueAt] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchReminders = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axios.get(`${API_URL}/api/reminders${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReminders(response.data.reminders);
    } catch (error) {
      console.error('Failed to fetch reminders:', error);
      toast.error('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTitle.trim() || !newDueAt) return;

    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const dueDate = new Date(newDueAt).toISOString();
      
      await axios.post(
        `${API_URL}/api/reminders`,
        {
          title: newTitle.trim(),
          notes: newNotes.trim() || null,
          due_at: dueDate
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Reminder created');
      setNewTitle('');
      setNewNotes('');
      setNewDueAt('');
      setShowCreate(false);
      fetchReminders();
    } catch (error) {
      console.error('Failed to create reminder:', error);
      toast.error(error.response?.data?.detail || 'Failed to create reminder');
    } finally {
      setCreating(false);
    }
  };

  const markDone = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/reminders/${id}/done`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reminder completed');
      fetchReminders();
    } catch (error) {
      toast.error('Failed to update reminder');
    }
  };

  const dismissReminder = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/reminders/${id}/dismiss`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reminder dismissed');
      fetchReminders();
    } catch (error) {
      toast.error('Failed to dismiss reminder');
    }
  };

  const reopenReminder = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/reminders/${id}/reopen`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reminder reopened');
      fetchReminders();
    } catch (error) {
      toast.error('Failed to reopen reminder');
    }
  };

  const deleteReminder = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/reminders/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Reminder deleted');
      fetchReminders();
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  const isOverdue = (dueAt) => new Date(dueAt) < new Date();

  // Set default due date to tomorrow at 9 AM
  useEffect(() => {
    if (showCreate && !newDueAt) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      setNewDueAt(formatDateForInput(tomorrow));
    }
  }, [showCreate, newDueAt]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/chat" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm">Back to Chat</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            <h1 className="text-xl font-semibold">Reminders</h1>
          </div>
          <Button 
            onClick={() => setShowCreate(true)} 
            size="sm"
            className="gap-2"
            data-testid="create-reminder-btn"
          >
            <Plus className="h-4 w-4" />
            New Reminder
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-3xl">
        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border pb-4">
          {[
            { value: 'scheduled', label: 'Upcoming', icon: Clock },
            { value: 'done', label: 'Done', icon: CheckCircle2 },
            { value: 'dismissed', label: 'Dismissed', icon: XCircle },
            { value: 'all', label: 'All', icon: Bell }
          ].map(({ value, label, icon: Icon }) => (
            <Button
              key={value}
              variant={filter === value ? 'default' : 'ghost'}
              size="sm"
              onClick={() => { setFilter(value); setLoading(true); }}
              className="gap-2"
              data-testid={`filter-${value}-btn`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </Button>
          ))}
        </div>

        {/* Create Reminder Modal */}
        <AnimatePresence>
          {showCreate && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
              onClick={() => setShowCreate(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-background border border-border rounded-lg p-6 w-full max-w-md shadow-xl"
              >
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Bell className="h-5 w-5 text-primary" />
                  New Reminder
                </h2>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Title *</label>
                    <Input
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      placeholder="What do you need to remember?"
                      autoFocus
                      data-testid="reminder-title-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Notes (optional)</label>
                    <Input
                      value={newNotes}
                      onChange={(e) => setNewNotes(e.target.value)}
                      placeholder="Additional details..."
                      data-testid="reminder-notes-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Due Date & Time *</label>
                    <Input
                      type="datetime-local"
                      value={newDueAt}
                      onChange={(e) => setNewDueAt(e.target.value)}
                      min={formatDateForInput(new Date())}
                      data-testid="reminder-due-input"
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCreate(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={!newTitle.trim() || !newDueAt || creating}
                      className="flex-1 gap-2"
                      data-testid="save-reminder-btn"
                    >
                      {creating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                      Create
                    </Button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Reminders List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : reminders.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground mb-2">
              {filter === 'scheduled' ? 'No upcoming reminders' : 
               filter === 'done' ? 'No completed reminders' :
               filter === 'dismissed' ? 'No dismissed reminders' :
               'No reminders yet'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {filter === 'scheduled' && 'Create a reminder to stay on top of your tasks'}
            </p>
            {filter === 'scheduled' && (
              <Button onClick={() => setShowCreate(true)} variant="outline" className="gap-2">
                <Plus className="h-4 w-4" />
                Create your first reminder
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <AnimatePresence mode="popLayout">
              {reminders.map((reminder) => (
                <motion.div
                  key={reminder.id}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className={`p-4 border rounded-lg transition-colors ${
                    reminder.status === 'done' 
                      ? 'bg-emerald-500/5 border-emerald-500/20' 
                      : reminder.status === 'dismissed'
                      ? 'bg-muted/30 border-border/50'
                      : isOverdue(reminder.due_at)
                      ? 'bg-red-500/5 border-red-500/30'
                      : 'bg-background border-border hover:border-primary/30'
                  }`}
                  data-testid={`reminder-item-${reminder.id}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Status indicator / Action button */}
                    <div className="flex-shrink-0 pt-0.5">
                      {reminder.status === 'scheduled' ? (
                        <button
                          onClick={() => markDone(reminder.id)}
                          className="h-5 w-5 rounded-full border-2 border-primary/50 hover:border-primary hover:bg-primary/10 transition-colors"
                          title="Mark as done"
                          data-testid={`mark-done-${reminder.id}`}
                        />
                      ) : reminder.status === 'done' ? (
                        <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                      ) : (
                        <XCircle className="h-5 w-5 text-muted-foreground/50" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-medium ${
                        reminder.status === 'done' ? 'line-through text-muted-foreground' :
                        reminder.status === 'dismissed' ? 'text-muted-foreground' : ''
                      }`}>
                        {reminder.title}
                      </h3>
                      {reminder.notes && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {reminder.notes}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs">
                        <span className={`flex items-center gap-1 ${
                          reminder.status === 'scheduled' && isOverdue(reminder.due_at)
                            ? 'text-red-500'
                            : 'text-muted-foreground'
                        }`}>
                          {reminder.status === 'scheduled' && isOverdue(reminder.due_at) ? (
                            <AlertCircle className="h-3 w-3" />
                          ) : (
                            <Calendar className="h-3 w-3" />
                          )}
                          {formatDateTime(reminder.due_at)}
                        </span>
                        {reminder.status === 'scheduled' && (
                          <span className={`${
                            isOverdue(reminder.due_at) ? 'text-red-500 font-medium' : 'text-muted-foreground'
                          }`}>
                            {formatRelativeTime(reminder.due_at)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {reminder.status === 'scheduled' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => dismissReminder(reminder.id)}
                          className="h-8 w-8 text-muted-foreground hover:text-foreground"
                          title="Dismiss"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                      {(reminder.status === 'done' || reminder.status === 'dismissed') && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => reopenReminder(reminder.id)}
                          className="h-8 w-8 text-muted-foreground hover:text-foreground"
                          title="Reopen"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteReminder(reminder.id)}
                        className="h-8 w-8 text-muted-foreground hover:text-red-500"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
